import { ReservationResponse } from '@/repositories/ReservationRepository'
import {
  Box,
  Button,
  Center,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useState } from 'react'
import { FiCalendar, FiList } from 'react-icons/fi'
import { ReservationDetails } from '../../components/dashboard/ReservationDetails'
import {
  ReservationCalendar,
  ReservationFilters,
  ReservationList,
} from '../../components/features/reservations'
import {
  ReservationFilters as ReservationFiltersType,
  useReservations,
} from '../../hooks/useReservations'

const getFirstDayOfMonth = () => {
  const today = new Date().toISOString().split('T')[0]
  const timezoned = new Date(`${today}T03:00:00`)
  const firstDayOfMonth = new Date(
    timezoned.getFullYear(),
    timezoned.getMonth(),
    1
  )

  return firstDayOfMonth
}

export function ReservationsPage() {
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list')
  const [currentDate, setCurrentDate] = useState(
    new Date(`${new Date().toISOString().split('T')[0]}T03:00:00`)
  )
  const [selectedReservation, setSelectedReservation] =
    useState<ReservationResponse | null>(null)
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [filters, setFilters] = useState<ReservationFiltersType>({
    page: 1,
    limit: 15,
    sortBy: 'check_in_date',
    sortOrder: 'desc',
    search: '',
    date_range: {
      start: getFirstDayOfMonth(),
      end: currentDate,
    },
  })

  const { reservations, count, isLoading, error } = useReservations(filters)

  const handleViewDetails = (reservation: ReservationResponse) => {
    setSelectedReservation(reservation)
    setIsDetailsOpen(true)
  }

  if (isLoading && !reservations.length) {
    return (
      <Center h="200px">
        <VStack>
          <Text color="gray.500" fontSize="lg">
            Carregando reservas...
          </Text>
        </VStack>
      </Center>
    )
  }

  if (error) {
    return (
      <Center h="200px">
        <VStack>
          <Text color="red.500" fontSize="lg">
            Erro ao carregar reservas
          </Text>
          <Text color="gray.500" fontSize="sm">
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </Text>
        </VStack>
      </Center>
    )
  }

  return (
    <Box p={{ base: 2, md: 6 }}>
      <HStack justify="space-between" mb={6}>
        <Box>
          <Heading size="lg" mb={2}>
            Reservas
          </Heading>
          <Text color="gray.600">
            Visualize as reservas de suas propriedades.
          </Text>
        </Box>
        <HStack>
          <Button
            leftIcon={<Icon as={FiCalendar} />}
            colorScheme={viewMode === 'calendar' ? 'blue' : 'gray'}
            variant={viewMode === 'calendar' ? 'solid' : 'outline'}
            onClick={() => setViewMode('calendar')}
            size="sm"
          >
            Calendário
          </Button>
          <Button
            leftIcon={<Icon as={FiList} />}
            colorScheme={viewMode === 'list' ? 'blue' : 'gray'}
            variant={viewMode === 'list' ? 'solid' : 'outline'}
            onClick={() => setViewMode('list')}
            size="sm"
          >
            Lista
          </Button>
        </HStack>
      </HStack>

      {viewMode === 'calendar' ? (
        <ReservationCalendar
          reservations={reservations}
          currentDate={currentDate}
          onDateChange={setCurrentDate}
          onReservationClick={handleViewDetails}
        />
      ) : (
        <>
          <ReservationFilters
            filters={filters}
            onFiltersChange={(newFilters: Partial<ReservationFiltersType>) =>
              setFilters({ ...filters, ...newFilters, page: 1 })
            }
          />
          <ReservationList
            reservations={reservations}
            onViewDetails={handleViewDetails}
            isLoading={isLoading}
            page={filters.page || 1}
            limit={filters.limit || 15}
            totalCount={count}
            onPageChange={(page: number) => setFilters({ ...filters, page })}
          />
        </>
      )}

      {selectedReservation && isDetailsOpen && (
        <ReservationDetails
          reservation={selectedReservation}
          isOpen={isDetailsOpen}
          onClose={() => {
            setIsDetailsOpen(false)
            setSelectedReservation(null)
          }}
        />
      )}
    </Box>
  )
}
